import { useAppContext } from '@prisjakt/ui-app-context';
import { IconChevronRight } from '@prisjakt/ui/icon';
import React from 'react';
import { Link } from 'react-router-dom';
import {
  InternalNavigationScope,
  trackInternalNavigationClick,
} from 'src/globals/tracking/tracking';
import { useOurChoiceExperiment } from 'src/hooks/experiments';
import { Award } from 'src/types';
import { usePriceRowContext } from '../../../price-row-context';
import { OfferPaymentProviders } from '../payment-provider';
import { OurChoiceInfo } from './our-choice-info';
import { RecommendedRetailer } from './recommended-retailer';
import { StoreInfoList } from './store-info-list';
import { StoreReviews } from './store-reviews';
import * as S from './styles';

interface IStoreInfo {
  sotyBadge?: Award | null;
}

export const StoreInfo: React.FC<React.PropsWithChildren<
  React.PropsWithChildren<IStoreInfo>
>> = props => {
  const { sotyBadge } = props;
  const ourChoiceExperiment = useOurChoiceExperiment();
  const { brand, offer, isOurChoice } = usePriceRowContext();
  const { authorizedDealer, priceId, store, storeInfo } = offer;
  const { t } = useAppContext();

  if (!store) {
    return null;
  }

  return (
    <S.ExpandedInfo>
      <div>
        <S.StoreLink
          container={Link}
          containerProps={{
            to: store.pathName,
            title: store.name,
            'data-test': 'StoreInfoLink',
            'aria-label': t('product', 'store-info.more-about-store', {
              store_name: store.name,
            }),
            tabIndex: 0,
            onClick: () =>
              trackInternalNavigationClick({
                scope: InternalNavigationScope.STORE_GO_TO,
                nextPage: store.pathName,
              }),
          }}
        >
          {store.name}
          <IconChevronRight />
        </S.StoreLink>

        {store.userReviewSummary.countTotal &&
        store.userReviewSummary.countTotal > 0 ? (
          <StoreReviews
            reviewCount={store.userReviewSummary.countTotal}
            storeLink={store.pathName}
          />
        ) : null}

        <StoreInfoList storeInfo={storeInfo} />

        <S.HomeDelivery />
      </div>

      {sotyBadge?.logo ? (
        <S.SotyBadge
          imgUrl={sotyBadge.logo}
          name={sotyBadge.name}
          year={sotyBadge.year?.toString()}
        />
      ) : null}

      <S.GeneralInfo>
        {ourChoiceExperiment && isOurChoice && <OurChoiceInfo />}

        <S.LocationAndCurrencyInfo
          countryCode={store.countryCode}
          countryName={store.countryName}
          currency={store.currency}
          marketplace={Boolean(store.marketplace)}
          id={priceId || store.id.toString()}
          storeName={store.name}
          minify
          textSize="small"
        />

        {authorizedDealer && brand && offer.store && (
          <RecommendedRetailer
            id={brand.id}
            pathName={brand.pathName}
            logo={brand.logo}
            brandName={brand.name}
            storeName={offer.store.name}
          />
        )}

        {store && <OfferPaymentProviders store={store} showAll />}
      </S.GeneralInfo>
    </S.ExpandedInfo>
  );
};
